import { Platform } from "react-native"

import { logs, type AnyValueMap, SeverityNumber } from "@opentelemetry/api-logs"
import { init, ReactNativeOptions, ErrorEvent } from "@sentry/react-native"

import { isAxiosError } from "@treefort/lib/is-axios-error"
import isObject from "@treefort/lib/is-object"

import config from "../config"
import { isAxiosNetworkError } from "./logging"

/**
 * Initialize our logging service (Sentry)
 *
 * NOTE: This is in a separate module from "./logging" to avoid the circular
 * dependency that would otherwise result from importing
 * "./analytics/plugins/rudderstack"
 */
export const initializeLogging = (options?: ReactNativeOptions): void => {
  init({
    dsn: config.SENTRY_DSN,
    tracesSampleRate: config.SENTRY_TRACES_SAMPLE_RATE,
    _experiments: config.SENTRY_PROFILES_SAMPLE_RATE
      ? { profilesSampleRate: config.SENTRY_PROFILES_SAMPLE_RATE }
      : undefined,
    environment: config.ENV,
    release: config.SENTRY_RELEASE,
    dist: config.SENTRY_DIST,
    initialScope: { tags: { tenantId: config.TENANT_ID } },
    beforeSend: (event, hint) => {
      // HACK: @sentry/react-native@5.31.0 reverses all errors. We reverse them
      // back to avoid the root cause being logged as the captured error and the
      // captured error being logged as the root cause.
      if (Platform.OS === "ios" || Platform.OS === "android") {
        event.exception?.values?.reverse()
      }

      // Expo sets a "code" field on all of the errors that it generates. This
      // logic ensures that any such "code" fields make it through to Sentry.
      const code =
        isObject(hint.originalException) && hint.originalException.code
      if (typeof code === "number" || typeof code === "string") {
        event.tags ||= {}
        event.tags.code = code
      }

      // Log _all_ errors in development
      if (config.ENV === "development") {
        const error = hint.originalException || hint.syntheticException || event
        if (error instanceof Error && error.cause) {
          console.error(error.cause)
        }
        console.error(error)
        if (event.extra) {
          console.error(event.extra)
        }
      }

      // Send everything to signoz
      try {
        logSentryEventToSignoz(event)
      } catch (cause) {
        if (config.ENV === "development") {
          console.error(new Error("Failed to log to SigNoz", { cause }))
        }
      }

      // Selectively report errors to Sentry
      if (shouldSendEventToSentry(hint.originalException)) {
        return event
      } else {
        return null
      }
    },
    ...options,
  })
}

/**
 * Recursively check error causes for a known issue
 */
function isCausedBy(error: unknown, check: (error: unknown) => boolean) {
  if (check(error)) {
    return true
  }

  if (error instanceof Error && error.cause) {
    return isCausedBy(error.cause, check)
  }

  return false
}

/**
 * This utility determines whether an event is sent to Sentry or suppressed.
 *
 * We should be very careful about suppressing events, but in some circumstances
 * suppression is necessary in order to cut down on noise and ensure that real
 * errors get the attention they deserve. If an error meets _all_ of these
 * criteria then we should consider suppressing it from Sentry:
 *
 * 1. The error is understood - we know what causes it and what the effect is.
 * 2. The error is not addressable - there's nothing we can do to fix it and
 *    we've done what we can to mitigate the effects.
 * 3. The error provides us with no valuable information - there's nothing
 *    significant that we can learn from it.
 * 4. The error occurs frequently - enough to make it prominent in our logs.
 */
const shouldSendEventToSentry = (error: unknown): boolean => {
  // Logging can be disabled entirely via the PUBLIC_SENTRY_ENABLE_EVENTS env
  // variable (useful for development)
  if (!config.SENTRY_ENABLE_EVENTS) {
    return false
  }

  // Suppress network errors which are not actionable and occur all the time as
  // mobile users roam the earth.
  if (isCausedBy(error, isAxiosNetworkError)) {
    return false
  }

  // Suppress 404s. Broken links happen and we don't need to track that in
  // Sentry.
  if (
    isCausedBy(
      error,
      (error) => isAxiosError(error) && error.response?.status === 404,
    )
  ) {
    return false
  }

  // Suppress iOS playback errors that are caused by the network connection
  // dropping.
  if (
    isCausedBy(
      error,
      (error) =>
        error instanceof Error &&
        /AVPlayerItem.*code -(1001|1008|1009).*NSURLErrorDomain/.test(
          error.toString(),
        ),
    )
  ) {
    return false
  }

  // Suppress Android playback errors that are caused by the network connection
  // dropping.
  if (
    isCausedBy(
      error,
      (error) =>
        error instanceof Error &&
        /exoplayer.*HttpDataSourceException.*Unable to connect/.test(
          error.toString(),
        ),
    )
  ) {
    return false
  }

  // Suppress Expo E_VIDEO_TAGINCORRECT errors which are plentiful and harmless.
  // See: https://github.com/expo/expo/issues/18402
  if (
    isCausedBy(
      error,
      (error) => isObject(error) && error.code === "E_VIDEO_TAGINCORRECT",
    )
  ) {
    return false
  }

  // Suppress Expo E_AV_SEEKING "Seeking interrupted" errors which are plentiful
  // and harmless.
  if (
    isCausedBy(
      error,
      (error) => isObject(error) && error.code === "E_AV_SEEKING",
    )
  ) {
    return false
  }

  return true
}

const sentrySignozLogger = logs.getLogger("sentry-logger")

function logSentryEventToSignoz(event: ErrorEvent) {
  const body =
    event.message || event.exception?.values?.[0].value || "Sentry event"
  const attributes: AnyValueMap = {
    exceptions:
      event.exception?.values && JSON.stringify(event.exception?.values),
    "user.id": event.user?.id,
    "profile.id": event.tags?.profileId?.toString(),
  }

  switch (event.level) {
    case "error":
    case "fatal":
      sentrySignozLogger.emit({
        body,
        severityNumber: SeverityNumber.ERROR,
        severityText: "ERROR",
        attributes,
      })
      break
    case "warning":
      sentrySignozLogger.emit({
        body,
        severityNumber: SeverityNumber.WARN,
        severityText: "WARN",
        attributes,
      })
      break
    case "log":
    case "debug":
    case "info":
      sentrySignozLogger.emit({
        body,
        severityNumber: SeverityNumber.INFO,
        severityText: "INFO",
        attributes,
      })
      break
    default:
      sentrySignozLogger.emit({
        body,
        severityNumber: SeverityNumber.UNSPECIFIED,
        severityText: "UNSPECIFIED",
        attributes,
      })
  }
}
